/**
 * 1.3.0 Administration → Media portal's data hooks (stream P1's file): settings, companies and users, delivery specs,
 * upload rules, and what their forms pick from. Keys: queryKeys.portal*(), deliverySpec*(), uploadRules(); every key
 * starts with portalAdmin(), so invalidating that one refreshes the whole section.
 */
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DeliverySpecDTO, PortalActivityKind, PortalActivityPageDTO, PortalCompanyDTO, PortalEmailTextsDTO, PortalQuotaUsageListDTO, PortalSettingsDTO,
  PortalUserDTO, UploadRuleDTO,
} from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

export interface PortalChoicesDTO {
  qcProfiles: Array<{ id: string; name: string }>;
  collections: Array<{ id: string; name: string }>;
  categories: Array<{ id: string; name: string; color: string }>;
  fields: Array<{ id: string; label: string; type: string; required: boolean; options?: Array<{ value: string; label: string }> }>;
  storageLocations: Array<{ id: string; name: string; kind: string; site: string; usable: boolean }>;
  people: Array<{ id: string; name: string; email: string }>;
  groups: Array<{ id: string; name: string }>;
  roles: Array<{ key: string; name: string }>;
  uploadMaxBytes: number;
}

export interface PortalUploadRowDTO {
  id: string;
  kind: 'delivery' | 'open';
  state: string;
  user: string | null;
  title: string | null;
  placeholderCode: string | null;
  files: number;
  bytes: number;
  resultAssetId: string | null;
  createdAt: string;
  submittedAt: string | null;
  decidedAt: string | null;
}

/** What inviting (or inviting again) answers: the user, and the link to pass on when email is not set up. */
export interface PortalInviteResult { user: PortalUserDTO; inviteLink: string | null; emailed: boolean; expiresAt: string }

export const portalChoicesKey = () => [...queryKeys.portalAdmin(), 'choices'] as const;

export function usePortalSettings() {
  return useQuery({ queryKey: queryKeys.portalSettings(), queryFn: () => api<PortalSettingsDTO>('/api/v1/admin/portal/settings') });
}

export function usePortalChoices() {
  return useQuery({ queryKey: portalChoicesKey(), queryFn: () => api<PortalChoicesDTO>('/api/v1/admin/portal/choices'), staleTime: 30_000 });
}

export function usePortalCompanies() {
  return useQuery({ queryKey: queryKeys.portalCompanies(), queryFn: () => api<{ items: PortalCompanyDTO[] }>('/api/v1/admin/portal/companies') });
}

export function usePortalUsers(companyId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.portalUsers(companyId),
    queryFn: () => api<{ items: PortalUserDTO[] }>(`/api/v1/admin/portal/companies/${companyId}/users`),
    enabled: Boolean(companyId),
  });
}

export function usePortalCompanyUploads(companyId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.portalCompanyUploads(companyId),
    queryFn: () => api<{ items: PortalUploadRowDTO[] }>(`/api/v1/admin/portal/companies/${companyId}/uploads`),
    enabled: Boolean(companyId),
  });
}

export function useDeliverySpecs(enabled = true) {
  return useQuery({ queryKey: queryKeys.deliverySpecs(), queryFn: () => api<{ items: DeliverySpecDTO[] }>('/api/v1/admin/portal/specs'), enabled });
}

export function useUploadRules() {
  return useQuery({ queryKey: queryKeys.uploadRules(), queryFn: () => api<{ items: UploadRuleDTO[] }>('/api/v1/admin/portal/rules') });
}

/** Refreshes everything under Administration → Media portal (after any change there). */
export function usePortalAdminRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.portalAdmin() });
}

/* ---------------------------------------------------------------- 1.3.1 (Q1) */

export const portalUsageKey = () => [...queryKeys.portalAdmin(), 'usage'] as const;
export const portalEmailTextsKey = () => [...queryKeys.portalAdmin(), 'email-texts'] as const;
export const portalCompanyActivityKey = (companyId: string, kind: PortalActivityKind) => [...queryKeys.portalAdmin(), 'activity', companyId, kind] as const;

/** Every company's quota usage (the Companies & users bars). */
export function usePortalUsage() {
  return useQuery({ queryKey: portalUsageKey(), queryFn: () => api<PortalQuotaUsageListDTO>('/api/v1/admin/portal/usage'), staleTime: 15_000 });
}

/** Every supplier email's text now (override or default) and its placeholders. */
export function usePortalEmailTexts() {
  return useQuery({ queryKey: portalEmailTextsKey(), queryFn: () => api<PortalEmailTextsDTO>('/api/v1/admin/portal/email-texts') });
}

/** A company's activity, newest first, a page at a time (Show more follows nextCursor). */
export function usePortalCompanyActivity(companyId: string, kind: PortalActivityKind, limit = 50) {
  return useInfiniteQuery({
    queryKey: portalCompanyActivityKey(companyId, kind),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams({ limit: String(limit), kind });
      if (pageParam) q.set('before', pageParam);
      return api<PortalActivityPageDTO>(`/api/v1/admin/portal/companies/${companyId}/activity?${q}`);
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}
